/**
 * **A turbina das duas cartas de movimento** (gate V28H.V).
 *
 * O Arranque e a Sobremarcha. As duas empurram a mesma nave, e por isso
 * partilham a mesma peça: uma chama presa à traseira do casco. O que as separa é
 * o REGIME - a Sobremarcha mantém-na acesa cinco segundos, o Arranque
 * sobrecarrega-a durante o impulso - e não o desenho.
 *
 * ## A composição, e porque é simétrica em volta de um eixo só
 *
 * Ela esteve montada em dois plumes laterais e um leque de riscas, e o dono viu
 * o resultado: "muito assimétrico e estranho". Duas chamas laterais só ficam
 * simétricas se o casco estiver exactamente alinhado com o rumo, e o casco deste
 * jogo VIRA devagar até lá - portanto durante meia curva elas apontavam para
 * fora da nave. O leque de riscas tinha o mesmo defeito e mais um: riscas
 * paradas não são um rasto, são traços.
 *
 * O que ficou é **um eixo**:
 *
 * 1. o **halo** da tubeira, redondo, colado ao casco - é ele que faz a chama
 *    nascer da nave em vez de flutuar atrás dela;
 * 2. a **chama**, um cone alinhado com o eixo e centrado nele;
 * 3. o **núcleo**, mais curto e quase branco, dentro da chama - é a diferença
 *    entre uma mancha e uma coisa que queima;
 * 4. as **brasas**, cuspidas para trás e deixadas no MUNDO;
 * 5. e o **anel do salto**, uma onda fina que fica onde o Arranque começou.
 *
 * Tudo é simétrico em relação ao eixo do movimento, portanto não há lado errado
 * possível.
 *
 * ## Nada à frente do casco
 *
 * Um jogador aponta com aquela nave e dispara a partir dela. Tudo aqui nasce no
 * bordo de TRÁS e cresce para trás; o anel do salto é a única excepção, e ele
 * fica no sítio onde a nave já não está.
 */

import type Phaser from 'phaser';

const TEXTURE = {
  plume: 'card-move-plume',
  spark: 'card-move-spark',
  wind: 'card-move-wind',
} as const;

/** Quantas brasas o conjunto guarda, recicladas em roda. */
const SPARKS = 36;

/** Quanto tempo cada uma vive, e de quanto em quanto tempo a turbina cospe. */
const SPARK_LIFE_MS = 560;
const SPARK_INTERVAL_MS = 28;

/** E quantas o Arranque cospe de uma vez, no impulso. */
const DASH_BURST = 18;

/**
 * **A sobrecarga do Arranque**: quanto a turbina cresce no primeiro instante.
 *
 * Pedida pelo dono - "uma sobrecarga na turbina para dar impressão de salto" - e
 * é ela que dá a ANTECIPAÇÃO: sem um pico no arranque, um deslocamento constante
 * lê-se como uma coisa a ser arrastada.
 */
const DASH_OVERCHARGE = 2.4;

/** Quanto tempo esse pico dura antes de assentar na chama normal. */
const DASH_OVERCHARGE_MS = 200;

/**
 * **Quantas riscas de vento a rajada do arranque atira, e quanto elas duram.**
 *
 * Trinta e quatro, e um segundo e meio cada uma - por medida do dono. Elas
 * sobrevivem ao próprio avanço de propósito: o impulso dura 600 ms, portanto as
 * riscas continuam a atravessar o ecrã depois de a nave já ter parado, e é isso
 * que deixa a passagem no ar em vez de a cortar a meio.
 */
const WINDS = 34;
const WIND_MS = 1_500;

/**
 * De quanto em quanto tempo o avanço deixa mais riscas para trás.
 *
 * Elas eram TODAS cuspidas no primeiro instante, e o dono viu o resultado: um
 * molho que se afastava em bloco e ficava longe da nave. Ar não nasce todo de
 * uma vez - ele fica ao longo do caminho, e é isso que este intervalo faz.
 */
const WIND_INTERVAL_MS = 26;

/**
 * Um gerador determinista, para espalhar as brasas sem `Math.random`.
 *
 * A mesma razão que a turbulência da esfera tem: duas abas com o mesmo relógio
 * desenham o mesmo rasto, e uma captura de ecrã compara-se com a seguinte.
 */
function hash(seed: number): number {
  let x = (seed * 1_664_525 + 1_013_904_223) % 4_294_967_296;
  x = ((x ^ (x >>> 15)) * 2_246_822_519) % 4_294_967_296;
  return ((x ^ (x >>> 13)) % 100_000) / 100_000;
}

/**
 * O cone da chama: largo e quente na tubeira, afilado e transparente na ponta.
 *
 * Desenhado a apontar para a DIREITA e **centrado na altura**, porque quem o
 * desenha ancora-o no eixo do movimento: um cone com o eixo fora do meio da
 * textura é um cone que puxa a chama para um lado.
 *
 * Branco, e tingido por quem desenha: a cor é a que o jogador escolheu, e assar
 * uma textura por cor seria assar uma textura por cor.
 */
function makePlume(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const width = 256;
  const height = 128;
  const texture = scene.textures.createCanvas(key, width, height);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, width, height);
  const midY = height / 2;
  /*
   * Quatro camadas encaixadas, da mais larga e ténue à mais estreita e opaca. É
   * a sobreposição delas que dá a rampa de calor sem um degradê radial.
   */
  for (const [reach, thickness, alpha] of [
    [width, midY * 0.9, 0.3],
    [width * 0.82, midY * 0.62, 0.42],
    [width * 0.58, midY * 0.38, 0.6],
    [width * 0.3, midY * 0.2, 0.95],
  ] as const) {
    const gradient = context.createLinearGradient(width - reach, 0, width, 0);
    gradient.addColorStop(0, 'rgba(255,255,255,0)');
    gradient.addColorStop(0.55, `rgba(255,255,255,${alpha * 0.55})`);
    gradient.addColorStop(1, `rgba(255,255,255,${alpha})`);
    context.fillStyle = gradient;
    context.beginPath();
    context.moveTo(width, midY - thickness);
    /* Duas curvas espelhadas: a silhueta é simétrica por construção. */
    context.quadraticCurveTo(width - reach * 0.45, midY - thickness * 0.55, width - reach, midY);
    context.quadraticCurveTo(
      width - reach * 0.45,
      midY + thickness * 0.55,
      width,
      midY + thickness,
    );
    context.closePath();
    context.fill();
  }
  texture.refresh();
}

/**
 * A brasa: um ponto redondo que apaga do centro para fora.
 *
 * Pequena de propósito. O que faz um rasto ler-se como fogo são MUITAS pequenas
 * com tempos diferentes; poucas e grandes leem-se como bolas.
 */
function makeSpark(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 64;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const glow = context.createRadialGradient(middle, middle, 0, middle, middle, middle);
  glow.addColorStop(0, 'rgba(255,255,255,1)');
  glow.addColorStop(0.35, 'rgba(255,255,255,0.6)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = glow;
  context.fillRect(0, 0, size, size);
  texture.refresh();
}

/**
 * **A risca de vento** (gate V28H.V).
 *
 * Aqui esteve um cone de choque triangular, e o dono viu-o: "está muito
 * estático". E estava, por construção - ele nascia no sítio do arranque e ficava
 * lá a abrir, portanto era uma forma a inchar e não ar a passar.
 *
 * O que ficou é o contrário: riscas rectas e finas que **viajam**, para trás e
 * depressa, cada uma com a sua velocidade. O que dá a sensação de vento não é a
 * forma, é o movimento delas contra o fundo parado.
 *
 * Afilada nas duas pontas, para não ter princípio nem fim marcados - uma risca
 * com topo lê-se como um objecto, e isto é ar.
 */
function makeWind(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const width = 256;
  const height = 16;
  const texture = scene.textures.createCanvas(key, width, height);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, width, height);
  const midY = height / 2;
  const gradient = context.createLinearGradient(0, 0, width, 0);
  gradient.addColorStop(0, 'rgba(255,255,255,0)');
  gradient.addColorStop(0.45, 'rgba(255,255,255,0.85)');
  gradient.addColorStop(0.6, 'rgba(255,255,255,0.9)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = gradient;
  context.beginPath();
  context.moveTo(0, midY);
  context.quadraticCurveTo(width * 0.5, midY - midY * 0.85, width, midY);
  context.quadraticCurveTo(width * 0.5, midY + midY * 0.85, 0, midY);
  context.closePath();
  context.fill();
  texture.refresh();
}

/** Gera as três texturas, uma vez por cena. Idempotente. */
export function ensureMovementTextures(scene: Phaser.Scene): void {
  makePlume(scene, TEXTURE.plume);
  makeSpark(scene, TEXTURE.spark);
  makeWind(scene, TEXTURE.wind);
}

/** O que um casco entrega, e nada mais do que isto. */
export interface MovementFxState {
  readonly x: number;
  readonly y: number;
  /** Para onde o casco APONTA - o mesmo rumo com que ele é desenhado. */
  readonly heading: number;
  /** O raio do casco desenhado: tudo aqui é medido a partir dele. */
  readonly hullRadius: number;
  /** A Sobremarcha está ligada. */
  readonly haste: boolean;
  /** O Arranque está a avançar. */
  readonly dash: boolean;
  /**
   * **A cor que o jogador escolheu.**
   *
   * "A cor do fogo muda conforme o utilizador escolhe", e é a mesma regra que
   * vale para todos os efeitos de carta desde que o selector existe: quem olha
   * escolhe a cor do que vê, incluindo o que sai da nave do adversário.
   */
  readonly tint: number;
}

/**
 * **A turbina de UM casco.**
 *
 * Uma instância por cadeira. Os objectos nascem à primeira vez que fazem falta e
 * depois são **escondidos** em vez de destruídos: a Sobremarcha dura cinco
 * segundos e volta, e o Arranque volta de três em três.
 */
export class MovementFx {
  private readonly scene: Phaser.Scene;
  private readonly blendAdd: number;
  private readonly depth: number;
  private halo: Phaser.GameObjects.Image | null = null;
  private plume: Phaser.GameObjects.Image | null = null;
  private core: Phaser.GameObjects.Image | null = null;
  private winds: Phaser.GameObjects.Image[] = [];
  /**
   * As riscas de vento, em coordenadas do MUNDO e com velocidade própria.
   *
   * Guardadas, como as brasas, e por mais uma razão: elas ANDAM. Uma risca
   * parada é a forma estática que o dono reprovou; o que a faz ler-se como ar a
   * passar é ela atravessar o ecrã enquanto apaga.
   */
  private readonly gust: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    bornAt: number;
    length: number;
    heading: number;
  }[] = [];
  private sparks: Phaser.GameObjects.Image[] = [];
  /**
   * O estado de cada brasa, em coordenadas do MUNDO.
   *
   * Guardado, ao contrário de tudo o resto: uma brasa nasce num sítio e fica lá
   * a apagar enquanto a nave se afasta. Uma função do relógio desenharia trinta
   * e seis brasas coladas ao casco, que é o contrário de um rasto.
   */
  private readonly ember: { x: number; y: number; bornAt: number; size: number }[] = [];
  private nextEmber = 0;
  private nextGust = 0;
  private lastEmitAt = 0;
  private lastGustAt = 0;
  /** Onde e quando este avanço começou, para o anel e para a sobrecarga. */
  private dash: { x: number; y: number; at: number } | null = null;

  constructor(scene: Phaser.Scene, blendAdd: number, depth: number) {
    this.scene = scene;
    this.blendAdd = blendAdd;
    this.depth = depth;
  }

  update(state: MovementFxState | null, nowMs: number, deltaMs: number): void {
    if (state?.dash === true && this.dash === null) {
      /* O impulso acabou de começar: o anel fica AQUI, e as brasas saem já. */
      this.build();
      this.dash = { x: state.x, y: state.y, at: nowMs };
      this.emit(state, nowMs, DASH_BURST, 1.3);
      this.gustEmit(state, nowMs, 3);
    }
    if (state === null || !state.dash) {
      this.dash = null;
    }

    if (state === null || (!state.haste && !state.dash)) {
      this.hideThruster();
      /* As brasas e o anel continuam a apagar: já não pertencem à nave. */
      this.advanceEmbers(nowMs, null);
      this.advanceGusts(nowMs, null, deltaMs);
      return;
    }
    this.build();

    /*
     * **A SOBRECARGA.** No princípio do impulso a turbina abre quase duas vezes
     * e meia e fecha depressa - `(1 - t)` ao cubo, a mesma queda que a explosão
     * usa: rápida no princípio, macia no fim. É um pico, e não um degrau.
     */
    const surge =
      this.dash === null
        ? 1
        : 1 +
          (DASH_OVERCHARGE - 1) *
            (1 - Math.min(1, (nowMs - this.dash.at) / DASH_OVERCHARGE_MS)) ** 3;
    const back = { x: -Math.cos(state.heading), y: -Math.sin(state.heading) };
    /*
     * A tubeira: **no bordo do casco**, sobre o eixo. Tudo o que se desenha sai
     * daqui, portanto não há como uma peça ficar de um lado e outra do outro.
     */
    const nozzle = {
      x: state.x + back.x * state.hullRadius * 0.52,
      y: state.y + back.y * state.hullRadius * 0.52,
    };
    const breathe = 1 + Math.sin(nowMs / 80) * 0.08;

    this.halo
      ?.setVisible(true)
      .setPosition(nozzle.x, nozzle.y)
      .setDisplaySize(state.hullRadius * 0.9 * surge, state.hullRadius * 0.9 * surge)
      .setTint(state.tint)
      .setAlpha(0.5 * Math.min(1, surge));
    this.plume
      ?.setVisible(true)
      .setPosition(nozzle.x, nozzle.y)
      .setRotation(state.heading)
      /*
       * **O dobro do comprimento e mais trinta por cento de largura** (V28H.V),
       * por medida do dono: 1,9 -> 3,8 raios de casco de comprimento, e
       * 0,86 -> 1,12 de largura.
       */
      .setDisplaySize(
        state.hullRadius * 3.8 * surge * breathe,
        state.hullRadius * 1.12 * Math.min(surge, 1.5),
      )
      .setTint(state.tint)
      .setAlpha(0.78);
    this.core
      ?.setVisible(true)
      .setPosition(nozzle.x, nozzle.y)
      .setRotation(state.heading)
      /* O núcleo acompanha a chama, na mesma proporção de sempre. */
      .setDisplaySize(
        state.hullRadius * 2.1 * surge * breathe,
        state.hullRadius * 0.52 * Math.min(surge, 1.4),
      )
      /* Quase branco: a cor entra pelas bordas da chama, e o meio queima. */
      .setTint(0xff_ff_ff)
      .setAlpha(0.9);

    /*
     * **O vento fica ONDE a nave passa**, e por isso é emitido a cada quadro do
     * avanço em vez de todo no princípio. O corredor forma-se atrás dela, tique
     * a tique, e é isso que o liga ao percurso em vez de o deixar num molho.
     */
    if (state.dash && nowMs - this.lastGustAt >= WIND_INTERVAL_MS) {
      this.lastGustAt = nowMs;
      this.gustEmit(state, nowMs, 2);
    }
    if (state.haste && nowMs - this.lastEmitAt >= SPARK_INTERVAL_MS) {
      /*
       * Por TEMPO e não por quadro: um monitor a 144 Hz produziria duas vezes e
       * meia mais brasas do que um a 60, e o rasto seria outra coisa em cada
       * máquina.
       */
      this.lastEmitAt = nowMs;
      this.emit(state, nowMs, 2, 0.45);
    }
    this.advanceEmbers(nowMs, state.tint);
    this.advanceGusts(nowMs, state.tint, deltaMs);
  }

  /** Põe brasas atrás do casco, no MUNDO, espalhadas em volta do eixo. */
  private emit(state: MovementFxState, nowMs: number, count: number, spread: number): void {
    const back = { x: -Math.cos(state.heading), y: -Math.sin(state.heading) };
    const side = { x: -back.y, y: back.x };
    for (let index = 0; index < count; index += 1) {
      const seed = this.nextEmber + index;
      /*
       * Simétrico em volta do eixo: o desvio é tão provável para um lado como
       * para o outro, porque o hash é centrado em zero.
       */
      const lateral = (hash(seed) - 0.5) * state.hullRadius * spread;
      const behind = state.hullRadius * (0.55 + hash(seed * 3 + 1) * 0.6 * spread);
      const slot = (this.nextEmber + index) % SPARKS;
      this.ember[slot] = {
        x: state.x + back.x * behind + side.x * lateral,
        y: state.y + back.y * behind + side.y * lateral,
        bornAt: nowMs,
        size: state.hullRadius * (0.1 + hash(seed * 7 + 2) * 0.14),
      };
    }
    this.nextEmber = (this.nextEmber + count) % SPARKS;
  }

  /** Apaga e encolhe o que já foi cuspido. Nada nasce aqui. */
  private advanceEmbers(nowMs: number, tint: number | null): void {
    for (const [index, spark] of this.sparks.entries()) {
      const state = this.ember[index];
      if (state === undefined) {
        spark.setVisible(false);
        continue;
      }
      const life = (nowMs - state.bornAt) / SPARK_LIFE_MS;
      if (life >= 1 || life < 0) {
        spark.setVisible(false);
        continue;
      }
      const size = Math.max(1, state.size * (1 - life * 0.65));
      spark
        .setVisible(true)
        .setPosition(state.x, state.y)
        .setDisplaySize(size, size)
        .setAlpha((1 - life) ** 1.5);
      if (tint !== null) {
        spark.setTint(tint);
      }
    }
  }

  /**
   * **O corredor de vento que o avanço deixa atrás de si.**
   *
   * Duas ou três riscas de cada vez, na posição ONDE A NAVE ESTÁ, e não num
   * leque à volta dela. O desvio lateral é pouco mais do que a largura do casco
   * - "o raio de spawn delas deve ser diminuído pelos lados, para ser spawnado
   * apenas onde a nave passar" - e o que as espalha ao longo do percurso é o
   * TEMPO, porque elas são emitidas quadro a quadro enquanto o avanço dura.
   *
   * A velocidade delas é baixa de propósito. Elas foram rápidas, e o resultado
   * foi um molho a fugir da nave: ar deslocado fica praticamente onde foi
   * deslocado, e é a nave que se afasta dele.
   *
   * **Tudo sai do raio do casco** - o desvio, o comprimento e a velocidade -
   * portanto uma nave grande deixa riscas proporcionalmente maiores e mais
   * afastadas, sem uma única medida em pixels neste ficheiro.
   */
  private gustEmit(state: MovementFxState, nowMs: number, count: number): void {
    const back = { x: -Math.cos(state.heading), y: -Math.sin(state.heading) };
    const side = { x: -back.y, y: back.x };
    for (let index = 0; index < count; index += 1) {
      const seed = this.nextGust + index;
      /* Dentro da largura do casco, e não à volta dela. */
      const lateral = (hash(seed * 5 + 1) - 0.5) * state.hullRadius * 1.15;
      /*
       * E claramente ATRÁS do casco: a ponta da frente da risca nasce a partir
       * de um raio inteiro para trás do centro, portanto nem a mais avançada
       * delas toca a nave.
       */
      const behind = state.hullRadius * (1.05 + hash(seed * 5 + 2) * 1.1);
      const speed = state.hullRadius * (1.2 + hash(seed * 5 + 3) * 1.8);
      const slot = (this.nextGust + index) % WINDS;
      this.gust[slot] = {
        x: state.x + side.x * lateral + back.x * behind,
        y: state.y + side.y * lateral + back.y * behind,
        vx: back.x * speed,
        vy: back.y * speed,
        bornAt: nowMs,
        /* Compridas: cinco a onze raios de casco, alinhadas com o percurso. */
        length: state.hullRadius * (5 + hash(seed * 5 + 4) * 6),
        heading: state.heading,
      };
    }
    this.nextGust = (this.nextGust + count) % WINDS;
  }

  /** As riscas andam, encolhem e apagam. Nada nasce aqui. */
  private advanceGusts(nowMs: number, tint: number | null, deltaMs: number): void {
    const step = Math.min(deltaMs, 64) / 1_000;
    for (const [index, wind] of this.winds.entries()) {
      const state = this.gust[index];
      if (state === undefined) {
        wind.setVisible(false);
        continue;
      }
      const life = (nowMs - state.bornAt) / WIND_MS;
      if (life >= 1 || life < 0) {
        wind.setVisible(false);
        continue;
      }
      /* Integrada por TEMPO: a mesma risca percorre o mesmo caminho a 60 e a 144. */
      state.x += state.vx * step;
      state.y += state.vy * step;
      wind
        .setVisible(true)
        .setPosition(state.x, state.y)
        /*
         * Pelo rumo com que NASCEU, e não recalculado da velocidade: uma risca
         * lenta tem uma velocidade quase nula, e `atan2` sobre ruído dá um
         * ângulo que salta de quadro para quadro.
         */
        .setRotation(state.heading)
        .setDisplaySize(state.length * (1 - life * 0.3), Math.max(1, state.length * 0.045))
        /*
         * Apagam devagar no princípio e depressa no fim: com um segundo e meio
         * de vida, uma queda linear deixaria riscas quase invisíveis pousadas no
         * ecrã durante meio segundo.
         */
        .setAlpha((1 - life) ** 2.2);
      if (tint !== null) {
        wind.setTint(tint);
      }
    }
  }

  private hideThruster(): void {
    this.halo?.setVisible(false);
    this.plume?.setVisible(false);
    this.core?.setVisible(false);
  }

  private build(): void {
    if (this.plume !== null) {
      return;
    }
    ensureMovementTextures(this.scene);
    const layer = (key: string, originX: number) =>
      this.scene.add
        .image(0, 0, key)
        .setDepth(this.depth)
        .setBlendMode(this.blendAdd)
        .setOrigin(originX, 0.5)
        .setVisible(false);
    this.halo = layer(TEXTURE.spark, 0.5);
    this.plume = layer(TEXTURE.plume, 1);
    this.core = layer(TEXTURE.plume, 1);
    for (let index = 0; index < WINDS; index += 1) {
      /*
       * **Ancoradas pela PONTA da frente** (gate V28H.V), e não pelo meio.
       *
       * Centradas, metade do comprimento ficava à frente do ponto onde nasciam -
       * e com riscas de cinco a onze raios de casco isso punha ar a passar
       * DIANTE da nave, que é o contrário do que ele é. Com a âncora na ponta,
       * uma risca só existe para trás do sítio onde nasceu.
       */
      this.winds.push(layer(TEXTURE.wind, 1));
    }
    for (let index = 0; index < SPARKS; index += 1) {
      this.sparks.push(layer(TEXTURE.spark, 0.5));
    }
  }

  /** Larga tudo. Para o `SHUTDOWN` da cena e para a saída de um participante. */
  destroy(): void {
    this.halo?.destroy();
    this.halo = null;
    this.plume?.destroy();
    this.plume = null;
    this.core?.destroy();
    this.core = null;
    for (const wind of this.winds) {
      wind.destroy();
    }
    this.winds = [];
    this.gust.length = 0;
    for (const spark of this.sparks) {
      spark.destroy();
    }
    this.sparks = [];
    this.ember.length = 0;
    this.dash = null;
  }
}

/**
 * **A opacidade do casco de quem está sob o Manto** (gate V28H.V).
 *
 * Uma constante e não um número solto na cena, porque ela é uma decisão: o
 * contrato diz que o dono "deve continuar vendo uma silhueta translúcida para
 * controlar a própria nave". Baixa o suficiente para se ler como ausência, alta
 * o suficiente para se pilotar.
 *
 * Quem NÃO é da equipa não recebe este casco de todo - a sala omite-o do frame -
 * portanto isto nunca é o que esconde alguém de um adversário. É só o que diz ao
 * dono que ele está escondido.
 */
export const CLOAK_HULL_ALPHA = 0.32;
